import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  EmptyState,
  Icon,
  Input,
  Menu,
  MenuGroup,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
  OptionPanelActionRow,
  OptionPanelSection,
  Text,
  toastManager,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { SavedFilterRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { AnalyticsContext, IssueEvent, useAnalytics } from "#app/lib/analytics";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { parseStoredFilterState } from "#app/lib/filters/stored-filters";
import type {
  FilterConfig,
  FilterState,
  UseFiltersResult,
} from "#app/lib/filters/types";
import type { UseSavedFiltersResult } from "#app/lib/hooks/use-saved-filters";
import { summarizeFilterValue } from "./use-quick-filter-summary";
import { useQuickFilterDirectory } from "#app/lib/hooks/use-quick-filter-directory";

const NO_TEAM_IDS: string[] = [];

/**
 * What the Saved tab needs: the viewer's store for its category, and the
 * host's way of asking for a name. The dialog lives on the host, outside the
 * menu that closes when a row is pressed.
 */
export type SavedFiltersProps = Omittable<{
  store: UseSavedFiltersResult;
  /** Ask for a name and save the current filters as a new row. */
  onSaveRequest: () => void;
  /** Ask for a new name for an existing row. */
  onRenameRequest: (row: SavedFilterRow) => void;
}>;

const SUMMARY_PARTS = 3;

/**
 * One line describing a saved filter's whole snapshot — both editors — in the
 * quick-filter summary grammar: "Stage: 2 stages selected · Assignee: Me".
 * A filter whose field the config no longer knows falls back to the field key,
 * so a stale snapshot still says what it holds. Past three parts the rest fold
 * behind a count, so the row stays one line.
 */
function describeSavedFilter(
  config: FilterConfig,
  row: SavedFilterRow,
  maps: Parameters<typeof summarizeFilterValue>[2],
): string {
  const states: FilterState[] = [
    parseStoredFilterState(row.filters),
    parseStoredFilterState(row.advancedFilters),
  ];
  const parts: string[] = [];
  for (const state of states) {
    for (const block of state.blocks) {
      for (const filter of block.filters) {
        if (!filter.field) continue;
        const def = config.fields[filter.field];
        const label = def?.label ?? filter.field;
        const value = def ? summarizeFilterValue(def.type, filter.value, maps) : null;
        parts.push(value ? `${label}: ${value}` : label);
      }
    }
  }
  if (parts.length === 0) return "No filters";
  const shown = parts.slice(0, SUMMARY_PARTS);
  const hidden = parts.length - shown.length;
  return hidden > 0 ? `${shown.join(" · ")} · +${hidden} more` : shown.join(" · ");
}

function useSavedFilterSummaries(
  config: FilterConfig,
  rows: SavedFilterRow[],
): Map<string, string> {
  const workspace = useWorkspaceData();
  const directory = useQuickFilterDirectory(NO_TEAM_IDS);
  const maps = useMemo(() => ({ ...workspace, directory }), [workspace, directory]);
  return useMemo(
    () => new Map(rows.map((row) => [row.id, describeSavedFilter(config, row, maps)])),
    [config, rows, maps],
  );
}

/**
 * The actions on one saved filter, shared by both surfaces. Apply REPLACES
 * both editors with the snapshot. Update re-snapshots the row from the
 * editors as they stand — how an applied-then-edited filter is kept — and is
 * closed while there is nothing to snapshot.
 */
/** `issue.*_saved_filter` events: the screen's scope and the list's
 *  surface, never the filter's name. */
function useTrackSavedFilter(via: string) {
  const { track } = useAnalytics();
  const scope = useIssueFiltersOptional()?.analyticsScope ?? "unknown";
  return (
    event: (typeof IssueEvent)[keyof typeof IssueEvent],
    data: Record<string, unknown> = {},
  ) =>
    track({
      context: AnalyticsContext.Issue,
      event,
      data: { via, scope, ...data },
    });
}

function useSavedFilterActions(
  filters: UseFiltersResult,
  store: UseSavedFiltersResult,
  via: string,
) {
  const trackSaved = useTrackSavedFilter(via);
  const canSnapshot = filters.activeFilterCount > 0;
  return {
    canSnapshot,
    apply: (row: SavedFilterRow) => {
      filters.applyState(
        parseStoredFilterState(row.filters),
        parseStoredFilterState(row.advancedFilters),
      );
      trackSaved(IssueEvent.ApplySavedFilter, {
        savedCount: store.savedFilters.length,
      });
    },
    update: async (row: SavedFilterRow) => {
      const updated = await store.updateSavedFilter(row.id, {
        filters: filters.filterState,
        advancedFilters: filters.advancedState,
      });
      if (updated) {
        trackSaved(IssueEvent.UpdateSavedFilter, {
          count: filters.activeFilterCount,
        });
        // The row looks the same after it takes the current filters, so the
        // toast confirms the write.
        toastManager.add({ title: `Updated “${row.name}”`, type: "success" });
      }
    },
    remove: (row: SavedFilterRow) => {
      void store.deleteSavedFilter(row.id).then((ok) => {
        if (ok) trackSaved(IssueEvent.DeleteSavedFilter);
      });
    },
  };
}

/** The two-line face a saved filter row wears: name over what it holds. */
function SavedFilterFace({ row, summary }: { row: SavedFilterRow; summary: string | undefined }) {
  return (
    <Box direction="column" fill="flex" minWidth={0}>
      <Text size="sm" truncate>
        {row.name}
      </Text>
      <Text size="xs" variant="muted" truncate>
        {summary}
      </Text>
    </Box>
  );
}

/**
 * First-run empty state of the Saved tab: the app's standard empty grammar,
 * with the save as its CTA when there is something to save.
 */
function SavedFiltersEmpty({
  canSave,
  onSave,
}: {
  canSave: boolean;
  onSave: () => void;
}) {
  return (
    <EmptyState
      size="sm"
      media="icon"
      icon="bookmark"
      title="No saved filters yet"
      description={
        canSave
          ? "Save the filters you have applied to come back to them later."
          : "Apply some filters, then save them to come back to them later."
      }
      actions={
        <Button size="sm" variant="outline" disabled={!canSave} onClick={onSave}>
          <Icon name="plus" size="lg" />
          <Text size="sm" weight="medium">
            Save current filters
          </Text>
        </Button>
      }
    />
  );
}

/**
 * The Saved tab's body in the toolbar MENU. Rows are the saved filters, name
 * over summary, and each opens its actions beside it on hover — Apply, Update
 * with current filters, Rename, Delete — the way the Simple tab's rows open
 * their values. The list is the whole page, so it can run long and scroll
 * with the popup. The save row heads it and rests disabled while there is
 * nothing to save.
 */
export function SavedFiltersMenuPage({
  config,
  filters,
  store,
  onSaveRequest,
  onRenameRequest,
}: SavedFiltersProps & { config: FilterConfig; filters: UseFiltersResult }) {
  const summaries = useSavedFilterSummaries(config, store.savedFilters);
  const actions = useSavedFilterActions(filters, store, "filter_panel");

  if (store.savedFilters.length === 0) {
    return <SavedFiltersEmpty canSave={actions.canSnapshot} onSave={onSaveRequest} />;
  }

  return (
    <>
      {/* `lg`: the same 32px rung the Simple tab's rows sit on, so switching
          tabs never reflows the row rhythm. */}
      <MenuItem size="lg" disabled={!actions.canSnapshot} onClick={onSaveRequest}>
        <Icon name="plus" size="sm" color="muted" />
        <Text size="sm">Save current filters…</Text>
      </MenuItem>
      <MenuSeparator />
      <MenuGroup>
        {store.savedFilters.map((row) => (
          <MenuSub key={row.id}>
            <MenuSubTrigger size="lg">
              <Icon name="bookmark" size="sm" color="muted" />
              <SavedFilterFace row={row} summary={summaries.get(row.id)} />
            </MenuSubTrigger>
            <MenuSubPopup width="action">
              <MenuItem onClick={() => actions.apply(row)}>
                <Icon name="filter" size="sm" color="muted" />
                <Text size="sm">Apply</Text>
              </MenuItem>
              <MenuItem
                disabled={!actions.canSnapshot}
                onClick={() => void actions.update(row)}
              >
                <Icon name="save" size="sm" color="muted" />
                <Text size="sm">Update with current filters</Text>
              </MenuItem>
              <MenuItem onClick={() => onRenameRequest(row)}>
                <Icon name="pencil" size="sm" color="muted" />
                <Text size="sm">Rename…</Text>
              </MenuItem>
              <MenuSeparator />
              <MenuItem variant="destructive" onClick={() => actions.remove(row)}>
                <Icon name="trash" size="sm" />
                <Text size="sm">Delete</Text>
              </MenuItem>
            </MenuSubPopup>
          </MenuSub>
        ))}
      </MenuGroup>
    </>
  );
}

/**
 * The Saved tab's body as a LIST — the mobile drawer and the Customize view
 * Filters page. A row press applies; the row's other actions sit behind a
 * trailing "…" that opens them as a menu, since touch has no hover and an
 * option-panel list discloses in place rather than beside itself.
 */
export function SavedFiltersSection({
  config,
  filters,
  store,
  onSaveRequest,
  onRenameRequest,
}: SavedFiltersProps & { config: FilterConfig; filters: UseFiltersResult }) {
  const summaries = useSavedFilterSummaries(config, store.savedFilters);
  const actions = useSavedFilterActions(filters, store, "saved_list");

  if (store.savedFilters.length === 0) {
    return <SavedFiltersEmpty canSave={actions.canSnapshot} onSave={onSaveRequest} />;
  }

  return (
    <OptionPanelSection>
      <OptionPanelActionRow
        icon="plus"
        label="Save current filters"
        disabled={!actions.canSnapshot}
        onClick={onSaveRequest}
      />
      {store.savedFilters.map((row) => (
        <Box key={row.id} direction="row" align="center" gap="xs">
          <Box fill="flex" minWidth={0} direction="column">
            <OptionPanelActionRow
              icon="bookmark"
              label={<SavedFilterFace row={row} summary={summaries.get(row.id)} />}
              onClick={() => actions.apply(row)}
            />
          </Box>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Actions for saved filter ${row.name}`}
                />
              }
            >
              <Icon name="ellipsis" size="sm" color="muted" />
            </MenuTrigger>
            <MenuPopup align="end" width="action">
              <MenuItem
                disabled={!actions.canSnapshot}
                onClick={() => void actions.update(row)}
              >
                <Icon name="save" size="sm" color="muted" />
                <Text size="sm">Update with current filters</Text>
              </MenuItem>
              <MenuItem onClick={() => onRenameRequest(row)}>
                <Icon name="pencil" size="sm" color="muted" />
                <Text size="sm">Rename…</Text>
              </MenuItem>
              <MenuSeparator />
              <MenuItem variant="destructive" onClick={() => actions.remove(row)}>
                <Icon name="trash" size="sm" />
                <Text size="sm">Delete</Text>
              </MenuItem>
            </MenuPopup>
          </Menu>
        </Box>
      ))}
    </OptionPanelSection>
  );
}

/** What the name dialog is for: a new snapshot, or a new name for a row. */
export type SavedFilterDialogIntent =
  | { kind: "create" }
  | { kind: "rename"; row: SavedFilterRow };

/** The dialog's React key: one per intent, so each open seeds its own name
 *  (a rename starts from the row's; a save starts blank) instead of
 *  inheriting the last. */
export function savedFilterDialogKey(intent: SavedFilterDialogIntent | null): string {
  if (!intent) return "closed";
  return `${intent.kind}:${intent.kind === "rename" ? intent.row.id : ""}`;
}

/**
 * The name prompt behind "Save current filters" and "Rename". Creating
 * snapshots BOTH editors as they stand when Save is pressed; renaming touches
 * the name alone. One AlertDialog at every breakpoint — bottom-stick covers
 * the mobile presentation, so there is no sheet fork. Keyed by the host on the
 * intent so a fresh open starts from the right name.
 */
export function SavedFilterNameDialog({
  intent,
  onClose,
  filters,
  store,
  via,
}: Omittable<{
  intent: SavedFilterDialogIntent | null;
  onClose: () => void;
  filters: UseFiltersResult;
  store: UseSavedFiltersResult;
  /** The host surface, for `issue.save_filter` / `rename_saved_filter`. */
  via: string;
}>) {
  const trackSaved = useTrackSavedFilter(via);
  const [name, setName] = useState(intent?.kind === "rename" ? intent.row.name : "");
  const [saving, setSaving] = useState(false);
  const renaming = intent?.kind === "rename";
  const count = filters.activeFilterCount;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving || !intent) return;
    setSaving(true);
    const result =
      intent.kind === "rename"
        ? await store.updateSavedFilter(intent.row.id, { name: trimmed })
        : await store.createSavedFilter({
            name: trimmed,
            filters: filters.filterState,
            advancedFilters: filters.advancedState,
          });
    setSaving(false);
    if (!result) return;
    if (intent.kind === "rename") {
      trackSaved(IssueEvent.RenameSavedFilter);
    } else {
      trackSaved(IssueEvent.SaveFilter, {
        count,
        savedCount: store.savedFilters.length + 1,
      });
    }
    onClose();
  };

  return (
    <AlertDialog open={intent !== null} onOpenChange={(next) => (next ? null : onClose())}>
      {/* The popup IS the form: a wrapper would sit outside the popup's flex
          column, and the clipped popup then cut the footer off. */}
      <AlertDialogPopup
        render={
          <Box
            as="form"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          />
        }
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{renaming ? "Rename saved filter" : "Save filters"}</AlertDialogTitle>
          <AlertDialogDescription>
            {renaming
              ? "Enter a new name for this saved filter."
              : count === 1
                ? "Name this filter to reuse it later."
                : `Name these ${count} filters to reuse them later.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogPanel>
          <Input
            aria-label="Saved filter name"
            placeholder="My active jobs"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </AlertDialogPanel>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button type="submit" disabled={!name.trim() || saving}>
            {renaming ? "Rename" : "Save"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
